import { queryOptions } from '@tanstack/react-query';
import type { usePermissions } from '@customerio/hydra-permissions/react';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { PlanFeature, type AugmentedAccount, type SearchResult } from '@/services/auth/types';
import { NAVIGATION_EXTRAS, NAVIGATION_LABELS } from './navigation-manifest';

export type PermissionGuard = Pick<ReturnType<typeof usePermissions>, 'hasPermission' | 'hasAnyPermission'> & {
  account: AugmentedAccount | null;
  hasFeatureFlag: (flag: AccountFeatureFlag) => boolean;
};

type GuardFunction = (guard: PermissionGuard) => boolean;

const ROUTE_GUARDS = [
  {
    test: (r) => r.includes('/analysis/deliverability'),
    guard: ({ account, hasFeatureFlag }) =>
      account?.plan_slug !== BUILDER_PLAN_SLUG && hasFeatureFlag(AccountFeatureFlag.DeliverabilityAnalytics),
  },
  // Routes that redirect builder plan users to billing upgrade
  {
    test: (r) =>
      r.includes('/journeys/analysis') ||
      r.includes('/journeys/campaigns') ||
      r.includes('/journeys/message-library') ||
      r.includes('/journeys/goals') ||
      r.includes('/journeys/objects') ||
      r.includes('/journeys/integrations') ||
      r.includes('/journeys/layouts') ||
      r.includes('/journeys/collections') ||
      r.includes('/journeys/settings/objects'),
    guard: ({ account }) => account?.plan_slug !== BUILDER_PLAN_SLUG,
  },
  {
    test: (r) => r.includes('/journeys/anonymous'),
    guard: ({ account }) => account?.planFeatures[PlanFeature.AnonymousInApp].enabled ?? false,
  },
  {
    test: (r) => r.includes('/journeys/integrations'),
    guard: ({ hasAnyPermission }) => hasAnyPermission('journeys/imports/view', 'journeys/exports/view'),
  },
  {
    test: (r) => r.includes('/journeys/imports'),
    guard: ({ hasPermission }) => hasPermission('journeys/imports/view'),
  },
  {
    test: (r) => r.includes('/journeys/exports'),
    guard: ({ hasPermission }) => hasPermission('journeys/exports/view'),
  },
  {
    test: (r) => r.includes('/design-studio/'),
    guard: ({ hasPermission }) => hasPermission('parcel/emails/view') && hasPermission('parcel/components/view'),
  },
  {
    test: (r) => r.includes('/agent'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.AiEnabled),
  },
  {
    test: (r) => r.includes('/agent/skills'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.AiSkills),
  },
  {
    test: (r) => /\/workspaces\/:workspaceId\/settings(\/|$)/.test(r),
    guard: ({ hasPermission }) => hasPermission('journeys/workspace_settings/view'),
  },
  {
    test: (r) => /\/workspaces\/:workspaceId\/journeys\/settings(\/|$)/.test(r),
    guard: ({ hasPermission }) => hasPermission('journeys/workspace_settings/view'),
  },
  {
    test: (r) => r.includes('/journeys/settings/api_credentials') || r.includes('/settings/api_credentials'),
    guard: ({ hasPermission }) => hasPermission('account/api_credentials/view'),
  },
  {
    test: (r) =>
      r.includes('/journeys/settings/webhook_configuration') || r.includes('/settings/webhook_configuration'),
    guard: ({ hasPermission }) => hasPermission('journeys/configuration_manager/view'),
  },
  {
    test: (r) => r.includes('/settings/ai-business-profile'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.AiEnabled),
  },
  {
    test: (r) => r.includes('/settings/llm_configuration'),
    guard: ({ hasFeatureFlag, hasPermission }) =>
      hasFeatureFlag(AccountFeatureFlag.LLMActionBYOK) && hasPermission('journeys/configuration_manager/view'),
  },
  {
    test: (r) =>
      r.startsWith('/settings/billing') || r.startsWith('/settings/subscription') || r.startsWith('/settings/receipts'),
    guard: ({ hasPermission }) => hasPermission('account/billing/view'),
  },
  {
    test: (r) => r.startsWith('/settings/audit-logs'),
    guard: ({ hasPermission }) => hasPermission('account/view'),
  },
  {
    test: (r) => r.startsWith('/settings/security') || r.startsWith('/settings/account'),
    guard: ({ hasPermission }) => hasPermission('account/view'),
  },
  {
    test: (r) => r.startsWith('/settings/roles'),
    guard: ({ hasPermission }) => hasPermission('account/team_members/view'),
  },
  {
    test: (r) => r.startsWith('/settings/api'),
    guard: ({ hasPermission }) => hasPermission('account/api_credentials/view'),
  },
] satisfies { test: (routeTemplate: string) => boolean; guard: GuardFunction }[];

function isAllowed(routeTemplate: string, guard: PermissionGuard): boolean {
  for (const { test, guard: guardFunction } of ROUTE_GUARDS) {
    if (test(routeTemplate) && !guardFunction(guard)) return false;
  }
  return true;
}

// Built once at module load — the manifest is the source of truth for navigable routes.
// Deduplicate by label so aliased paths (e.g. two routes sharing a "Workspace settings" label)
// don't produce duplicate results.
const NAVIGABLE_ROUTES: ReadonlyArray<{ label: string; routeTemplate: string }> = [
  ...[...Object.entries(NAVIGATION_LABELS), ...Object.entries(NAVIGATION_EXTRAS)]
    .reduce((map, [routeTemplate, label]) => {
      if (!map.has(label)) map.set(label, { label, routeTemplate });
      return map;
    }, new Map<string, { label: string; routeTemplate: string }>())
    .values(),
];

const MAX_RESULTS = 10;

function fuzzyMatch(pattern: string, string_: string): boolean {
  let pi = 0;
  for (let index = 0; index < string_.length && pi < pattern.length; index++) {
    if (string_[index] === pattern[pi]) pi++;
  }
  return pi === pattern.length;
}

export function searchNavigation({
  query,
  workspaceId,
  guard,
}: {
  query: string;
  workspaceId: number;
  guard: PermissionGuard | null;
}): Extract<SearchResult, { kind: 'navigation' }>[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2 || workspaceId === 0) return [];

  const matches: Extract<SearchResult, { kind: 'navigation' }>[] = [];
  for (const { label, routeTemplate } of NAVIGABLE_ROUTES) {
    if (!fuzzyMatch(q, label.toLowerCase())) continue;
    if (guard != null && !isAllowed(routeTemplate, guard)) continue;
    const url = routeTemplate.replace(':workspaceId', String(workspaceId));
    matches.push({ kind: 'navigation', title: label, url, doc_id: url });
  }

  // Prefer labels that start with the query over fuzzy mid-string matches
  matches.sort((a, b) => {
    const aStarts = a.title.toLowerCase().startsWith(q);
    const bStarts = b.title.toLowerCase().startsWith(q);
    if (aStarts && !bStarts) return -1;
    if (bStarts && !aStarts) return 1;
    return 0;
  });

  return matches.slice(0, MAX_RESULTS);
}

export function navigationSearchOptions(query: string, workspaceId: number, guard: PermissionGuard | null) {
  return queryOptions({
    // guard is intentionally excluded from the key — permission changes mid-session are rare
    // and typically accompanied by a full page reload that clears the cache anyway.
    queryKey: ['navigationSearch', query, workspaceId] as const,
    queryFn: async () => {
      // Artificial delay so instant local results don't feel broken to users expecting processing time
      await new Promise<void>((resolve) => setTimeout(resolve, 100));
      return searchNavigation({ query, workspaceId, guard });
    },
    staleTime: Infinity,
  });
}
